import { isPlatformBrowser } from "@angular/common";
import { HttpClient } from "@angular/common/http";
import {
    Component,
    DoCheck,
    ElementRef,
    HostListener,
    Inject,
    OnInit,
    PLATFORM_ID,
    ViewChild,
} from "@angular/core";
import { DomSanitizer } from "@angular/platform-browser";
import { FieldType, FieldTypeConfig } from "@ngx-formly/core";
import { cloneDeep } from "lodash";
import { Globals } from "../../../app.globals";
import {
    AsyncModalService,
    ButtonPresets,
} from "../../../services/shared/async-modal-service";
import { DataTableColumn } from "../../datatable/datatablecolumn/datatablecolumn.component";

@Component({
    standalone: false,
    templateUrl: "./file-uploader.component.html",
})
export class FormlyFileUploader extends FieldType<FieldTypeConfig> implements OnInit, DoCheck {
    @ViewChild("uploader") uploader: ElementRef;
    private _imageUrlModel: string = null;
    private asyncModalService: AsyncModalService;
    private _lastLoggedModelState: string = undefined;

    // Trace-only: logs whenever model[key] (what the table actually renders) changes,
    // to catch data disappearing after initial correct render. No behavior change.
    ngDoCheck() {
        const key = this.key as string;
        if (!key) return;
        const value = this.model ? this.model[key] : undefined;
        const length = Array.isArray(value) ? value.length : (value == null ? 'null/undefined' : typeof value);
        const state = key + '=' + length;
        if (state !== this._lastLoggedModelState) {
            this._lastLoggedModelState = state;
        }
    }

    constructor(
        @Inject(PLATFORM_ID) private platformId: object,
        public http: HttpClient,
        private globals: Globals,
        private sanitizer: DomSanitizer,
        asyncModalService: AsyncModalService
    ) {
        super();
        this.asyncModalService = asyncModalService;
    }

    @HostListener("window:beforeunload", ["$event"])
    beforeUnload($event) {
        if (
            !this.model.id &&
            this.props.baseUrl &&
            this.props.path &&
            this.formControl.value.length > 0
        ) {
            this.http
                .request("delete", this.props.baseUrl + "/folder", {
                    body: { url: this.props.path },
                })
                .subscribe();
        }
    }

    @HostListener("document:dragover", ["$event"])
    @HostListener("drop", ["$event"])
    onDragDropFileVerifyZone(event) {
        if (event.target.tagName == "INPUT" && event.target.type == "file") {
            event.stopPropagation();
        } else {
            event.preventDefault();
            event.dataTransfer.effectAllowed = "none";
            event.dataTransfer.dropEffect = "none";
        }
    }

    get imageUrlModel(): string {
        return this._imageUrlModel;
    }
    set imageUrlModel(url: string) {
        this._imageUrlModel = url;
    }

    ngOnInit() {
        if (isPlatformBrowser(this.platformId)) {
            if (!this.defaultOptions?.defaultValue)
                this.defaultOptions = {
                    ...this.defaultOptions,
                    defaultValue: [],
                };

            if (!this.model[this.key as string])
                this.model[this.key as string] = [];

            if (!this.formControl.value) this.formControl.setValue([]);

            if (this.props.multiple == null || this.props.multiple == undefined)
                this.field.props.multiple = true;

            this.installModelWatchTrace();

            if (this.props.url || this.props.baseUrl) this.reloadEntities();
        }
    }

    // Trace-only: prints the call stack whenever model[key] is reassigned, so we can
    // find exactly which code is clearing this field's array after it loads correctly.
    // No behavior change - the getter/setter just wraps the existing plain property.
    private installModelWatchTrace(): void {
        const key = this.key as string;
        const model: any = this.model;
        if (!key || !model || model['__watched_' + key]) return;

        model['__watched_' + key] = true;
        let currentValue = model[key];

        Object.defineProperty(model, key, {
            configurable: true,
            enumerable: true,
            get: () => currentValue,
            set: (v: any) => {
                currentValue = v;
            },
        });
    }

    process($event) {
        this.hideLoader();

        if (this.props.returnUrl && $event && $event.xhr) {
            (JSON.parse($event.xhr.responseText) || []).forEach((s) => {
                this._imageUrlModel = s.url;
            });

            return;
        }

        if (this.props.uploadComplete) {
            this.props.uploadComplete($event);
            if (this.props.forceReload) this.reloadEntities();
        } else this.reloadEntities();
    }

    errorDuringUploading($event) {
        this.hideLoader();

        if (this.props.uploadComplete) {
            $event.url = this.props.url;
            this.props.uploadComplete($event, this.props.url);
        }

        this.reloadEntities();
    }

    beforeUpload($event) {
        this.showLoader()
    }

    getEmptyMessageString() {
        return "No records found";
    }

    async reloadEntities() {
        var cachedFiles = [];
        let files: any = [];

        if (this.props.cacheDescriptions) {
            cachedFiles = Array.isArray(this.formControl.value)
                ? cloneDeep(this.formControl.value)
                : null;
        }

        this.showLoader();

        if (
            typeof this.formControl.value === "string" ||
            (this.formControl.value || []).length == 0
        ) {
            try {
                if (!(this.props.url.indexOf("undefined") > -1 || this.props.url.indexOf("00000000-0000-0000-0000-000000000000") > -1)) {
                    files = await this.http.get(this.props.url || this.props.baseUrl + "/getFiles?folder=" + (this.props.path || "")).toPromise();
                }
            } catch (err) {
                this.asyncModalService.getModalResponse("Failed to load the files");
            } finally {
                this.hideLoader();
            }
        }
        else if ((this.formControl.value || []).length > 0) {
            files = this.formControl.value;
            await this.delay(1000);
            this.hideLoader();
        }

        if (files) {
            (files || []).forEach((entity) => {
                if (this.props.cacheDescriptions) {
                    let cachedDoc = (cachedFiles || []).find(
                        (file) => file.fileName == entity.fileName
                    );
                    if (cachedDoc)
                        entity.fileDescription = cachedDoc.fileDescription;
                }
                entity.options = [
                    {
                        value: "Delete",
                        id: entity.id,
                        click: (controller: any, id: number, row: any) => {
                            this.deleteFile(row);
                        },
                    },
                ];
            });

            this.formControl.setValue(files);
        }

    }

    getModel(): any[] {
        if (this.props.tableEntities && this.props.tableEntities.length > 0)
            return this.props.tableEntities;
        return this.model[this.key as string];
    }

    uploadUrl() {
        var correctUrl =
            this.props.url || this.props.baseUrl + "?folder=" + this.props.path;
        return correctUrl;
    }

    async deleteFile(row: any) {
        var response = await this.asyncModalService.getModalResponse(
            "Are you sure you want to delete <b>" + row.originalFileName + "</b> file?",
            ButtonPresets.YesNo
        );

        if (response) {
            const url =
                row[this.props.tableColumns[0].value] || row.url || row.fileName;
            this.showLoader();
            this.http
                .request("delete", this.props.baseUrl || this.props.url + "/file", {
                    body: { Url: url },
                })
                .subscribe(
                    (data) => {
                        const model = this.getModel();

                        const index = model.findIndex(
                            (item) =>
                                (item.url || item.fileName) ===
                                (row.url || row.fileName)
                        );
                        model.splice(index, 1);
                        this.formControl.setValue(model);

                        // A delete is a user edit like any other: without this the control stays
                        // pristine/untouched, so removing the last file never showed the required
                        // error and the page didn't count as changed.
                        this.formControl.markAsDirty();
                        this.formControl.markAsTouched();
                        this.formControl.updateValueAndValidity();
                        this.field.options?.detectChanges?.(this.field);

                        if (this.props.change) this.props.change(this);
                    },
                    () => {
                        this.asyncModalService.getModalResponse("Failed to delete <b>" + row.originalFileName + "</b> file");
                    },
                    () => {
                        this.hideLoader();
                    }
                );
        }
    }

    onImageUpdated({ newPath, item, column }) {
        var model = this.getModel() || [];
        var key = (column as DataTableColumn).value;

        var modelItem = model.find((x) => x[key] == item[key]);

        if (newPath.indexOf("?") > -1)
            modelItem[key] = newPath + "&t=" + new Date().getTime();
        else modelItem[key] = newPath + "?t=" + new Date().getTime();

        this.formControl.setValue(model);
    }

    private showLoader() {
        this.props.loading = true;
    }

    private hideLoader() {
        this.props.loading = false;
    }

    private delay(ms: number) {
        return new Promise((resolve) => setTimeout(resolve, ms));
    }
}
